import React, { useEffect, useMemo, useRef, useState } from "react";

import { inspectEdgeTagCatalog, previewEdgeConnector } from "../../../api";
import NudgeNumber from "../../../components/NudgeNumber";
import Select from "../../../components/Select";
import TagPicker from "../../../components/TagPicker";
import EdgeModbusDeviceForm from "./EdgeModbusDeviceForm";
import EdgeNCDSensorForm from "./EdgeNCDSensorForm";
import {
  EDGE_CONNECTOR_KINDS,
  buildEdgeConnectorSpec,
  edgeConnectorTagEligibility,
  gpioBoardCapability,
} from "./edge-connector-spec";

const PROTOCOL_DESCRIPTIONS = {
  gpio: "Built-in digital inputs and outputs",
  "modbus-tcp": "Modbus devices over Ethernet",
  "modbus-rtu": "Modbus devices over a serial port",
  "modbus-rtu-over-tcp": "Modbus RTU frames carried over a TCP socket",
  "ncd-pr63-27c-usb": "NCD wireless vibration, ultrasound, and temperature sensors",
  opcua: "Connect a tag to an OPC UA node",
  mqtt: "Subscribe to or publish MQTT topics",
  "enip-class1": "Turck cyclic digital I/O",
  ethercat: "Beckhoff EK1818 digital I/O",
  profinet: "Turck PROFINET digital I/O",
};

// Per-kind editable defaults; string values feed NudgeNumber directly.
const DEFAULTS = {
  gpio: { bcmPin: "23", bias: "pull_up", debounceMs: "50", activeLow: false, safeState: false },
  opcua: { endpointUrl: "opc.tcp://", nodeId: "", namespaceUri: "", serverCertPEM: "" },
  mqtt: { url: "mqtts://", clientId: "controlseat-edge", topic: "", valuePath: "", tlsCAPEM: "" },
  "enip-class1": { host: "", tcpPort: "44818", targetUDPPort: "2222", localUDPHost: "0.0.0.0", channel: "0", rpiMs: "10", operationTimeoutMs: "1000", timeoutMultiplier: "1", originatorVendorId: "" },
  ethercat: { interfaceName: "", channel: "1", cycleTimeMs: "10", watchdogMs: "100", startupTimeoutMs: "3000", publishIntervalMs: "100", stationAddress: "4097", logicalAddress: "65536", deviceRevision: "0x00120000" },
  profinet: { interfaceName: "", stationName: "turck-bench", channel: "1", cycleTimeMs: "32", watchdogMs: "320", startupTimeoutMs: "5000", publishIntervalMs: "100" },
};

// The signed profiles share input/output internally, but exposing that as one
// generic "Data flow" question makes unrelated protocols look identical.
// Present the decision in each protocol's own vocabulary while preserving the
// exact runtime authority boundary underneath.
const protocolAccessControl = (kind) => {
  switch (kind) {
    case "opcua":
      return {
        label: "Access",
        options: [
          { value: "input", label: "Monitor node", description: "Subscribe to the node and update the tag. No writes are sent." },
          { value: "output", label: "Read and write node", description: "Subscribe to the node and verify tag writes by reading it back." },
        ],
      };
    case "mqtt":
      return {
        label: "MQTT action",
        options: [
          { value: "input", label: "Subscribe to topic", description: "Messages received on this topic update the tag." },
          { value: "output", label: "Publish tag changes", description: "Tag changes are published. MQTT does not provide device readback." },
        ],
      };
    case "enip-class1":
    case "profinet":
      return {
        label: "I/O type",
        options: [
          { value: "input", label: "Digital input", description: "The device input updates the tag." },
          { value: "output", label: "Digital output", description: "Tag changes update the device output." },
        ],
      };
    case "gpio":
      return {
        label: "I/O type",
        options: [
          { value: "input", label: "Digital input", description: "The hardware input updates the tag." },
          { value: "output", label: "Digital output", description: "Tag changes update the output and are confirmed by readback." },
        ],
      };
    default:
      return null;
  }
};

const ethercatPointOptions = Object.freeze([
  ...Array.from({ length: 8 }, (_, index) => ({ value: `input:${index + 1}`, label: `DI${index + 1} · Digital input` })),
  ...Array.from({ length: 4 }, (_, index) => ({ value: `output:${index + 1}`, label: `DO${index + 1} · Digital output` })),
]);

const REQUIRED_CONNECTOR_CAPABILITIES = [
  "connector-config-v1",
  "edge-tag-catalog-v1",
  "edge-tag-identity-v1",
];

export const edgeConnectorKindAvailability = (capabilities, gpioBoard) => {
  const advertised = new Set(Array.isArray(capabilities) ? capabilities : []);
  const connectorContractReady = REQUIRED_CONNECTOR_CAPABILITIES.every((capability) => advertised.has(capability));
  const boardCapability = gpioBoardCapability(gpioBoard?.profile);
  const gpioReady = gpioBoard?.status === "detected" && !!boardCapability && advertised.has(boardCapability);
  const selectable = [];
  const unavailable = [];

  EDGE_CONNECTOR_KINDS.forEach((item) => {
    const protocolReady = Array.isArray(item.capabilities)
      ? item.capabilities.some((capability) => advertised.has(capability))
      : advertised.has(item.capability);
    const supported = connectorContractReady && protocolReady && (item.value !== "gpio" || gpioReady);
    (supported ? selectable : unavailable).push(item);
  });
  return { selectable, unavailable, gpioReady };
};

const lastPathSegment = (path) => String(path || "").split("/").filter(Boolean).pop() || "";

// Canonical platform tag type → wire type used by both the cloud tag API and
// the runtime tag-deployment declaration.
const TAG_TYPE_WIRE = { boolean: "bool", integer: "int64", number: "float64", string: "string", json: "json" };

// Tag types each protocol can actually map (mirrors buildEdgeConnectorSpec's
// validation so an inline-created tag is always eligible for review).
const KIND_TAG_TYPES = {
  gpio: ["boolean"],
  "modbus-tcp": ["boolean", "integer", "number", "string"],
  "modbus-rtu": ["boolean", "integer", "number", "string"],
  "modbus-rtu-over-tcp": ["boolean", "integer", "number", "string"],
  opcua: ["boolean", "integer", "number", "string", "json"],
  mqtt: ["boolean", "integer", "number", "string", "json"],
  "enip-class1": ["boolean"],
  ethercat: ["boolean"],
  profinet: ["boolean"],
};

const TAG_TYPE_LABELS = {
  boolean: "Boolean", integer: "Integer", number: "Number", string: "String", json: "JSON",
};

const tagTypeLabel = (value) => TAG_TYPE_LABELS[value] || "This tag type";

const connectorTagError = ({ kind, tagType, direction }) => {
  if (!tagType) return "";
  const supportedTypes = KIND_TAG_TYPES[kind] || [];
  if (!supportedTypes.includes(tagType)) {
    const labels = supportedTypes.map((value) => TAG_TYPE_LABELS[value]).join(", ");
    return `${tagTypeLabel(tagType)} tags cannot be used here. Choose ${labels || "a supported tag type"}.`;
  }
  if (kind === "opcua" && direction === "output" && tagType === "json") {
    return "OPC UA JSON points can only be monitored. Choose Monitor node or use a scalar tag.";
  }
  return "";
};

const publicConnectionError = (error, gatewayName) => {
  const raw = String(error?.message || "").trim();
  const lower = raw.toLowerCase();
  if (/changed since|out of date|revision conflict|refresh .*try again/.test(lower)) {
    return "This connection changed after you opened it. Refresh the page and review your changes again.";
  }
  if (/already (used|claimed|reserved)|resource conflict|in use by/.test(lower)) {
    return "That device point is already used by another connection. Choose a different point or remove the existing connection.";
  }
  if (/offline|not online|unreachable|not connected/.test(lower)) {
    return `${gatewayName} is offline or unreachable. Check its connection and try again.`;
  }
  if (/timed out|timeout/.test(lower)) {
    return `${gatewayName} did not respond in time. Check the network or serial connection and try again.`;
  }
  if (/forbidden|permission|not authorized/.test(lower)) {
    return "You do not have permission to change connections on this device.";
  }
  return "Control Seat could not prepare this connection. Check the device settings and try again.";
};

const tagSlug = (value) => (String(value || "").trim().replace(/[^A-Za-z0-9_]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 64));

const initialConnectionSettings = (kind, draft) => {
  const defaults = { ...(DEFAULTS[kind] || {}) };
  const proposed = draft?.settings && typeof draft.settings === "object" ? draft.settings : {};
  const settings = { ...defaults, ...proposed };
  if (kind === "gpio" && draft) {
    return {
      ...settings,
      bcmPin: draft.bcm_pin == null ? settings.bcmPin : String(draft.bcm_pin),
      bias: draft.bias || settings.bias,
      debounceMs: draft.debounce_ms == null ? settings.debounceMs : String(draft.debounce_ms),
      activeLow: Boolean(draft.active_low),
      safeState: Boolean(draft.safe_state),
    };
  }
  return settings;
};

function FormField({ label, help, warning, className = "", children }) {
  return (
    <div className={`gwField${className ? ` ${className}` : ""}`}>
      <label className="gwLabel">{label}</label>
      {children}
      {help ? <small className="gwHint">{help}</small> : null}
      {warning ? <small className="gwHint isWarning" role="status">{warning}</small> : null}
    </div>
  );
}

function AdditionalFields({ children }) {
  return <div className="gwConnectionFields">{children}</div>;
}


function ProtocolFields({ kind, direction, setDirection, tagType, gpioBoard, values, setValue }) {
  if (kind === "gpio") {
    const availablePins = Array.isArray(gpioBoard?.available_pins) ? gpioBoard.available_pins : [];
    const iotLink = gpioBoard?.profile === "compulab-iot-link-dio-v1";
    const pinOptions = availablePins.map((pin) => ({
      value: String(pin.bcm),
      label: pin.label
        ? `${pin.label} — terminal ${pin.physical}`
        : `GPIO${pin.bcm} — physical pin ${pin.physical}${pin.note ? ` · ${pin.note}` : ""}`,
    }));
    return (
      <>
        {iotLink ? (
          <div className="gwNotice isSoft" role="note">
            Each terminal can be an input or output. For outputs, connect 6–24 V DC to terminal 1 and ground to terminal 9. Each channel supports up to 1 A.
          </div>
        ) : null}
        <div className="gwConnectionFields">
          <FormField label="Detected board" help="Detected automatically from the device.">
            <input className="gwInput" value={gpioBoard?.profile_label || "Not detected"} readOnly aria-readonly="true" />
          </FormField>
          <FormField label={iotLink ? "Digital channel" : "GPIO pin"}>
            <Select value={values.bcmPin} options={pinOptions} onChange={(value) => setValue("bcmPin", value)} />
          </FormField>
          {direction === "output" ? (
            <FormField label="Fallback output state" help="Used while the output is starting or unavailable.">
              <Select
                value={values.safeState ? "on" : "off"}
                options={[
                  { value: "off", label: "Off" },
                  { value: "on", label: "On" },
                ]}
                onChange={(value) => setValue("safeState", value === "on")}
              />
            </FormField>
          ) : null}
        </div>
        <AdditionalFields>
          {direction !== "output" && !iotLink ? (
            <>
            <FormField label="Input bias">
              <Select
                value={values.bias}
                options={[
                  { value: "pull_up", label: "Pull up" },
                  { value: "pull_down", label: "Pull down" },
                  { value: "disabled", label: "Disabled" },
                ]}
                onChange={(value) => setValue("bias", value)}
              />
            </FormField>
            <FormField label="Debounce (ms)">
              <NudgeNumber value={values.debounceMs} onChange={(value) => setValue("debounceMs", value)} min={0} max={5000} aria-label="Debounce (ms)" />
            </FormField>
            </>
          ) : null}
          <FormField label="Signal polarity" help="Use Active low only when the electrical signal is inverted.">
            <Select
              value={values.activeLow ? "active_low" : "active_high"}
              options={[
                { value: "active_high", label: "Active high" },
                { value: "active_low", label: "Active low" },
              ]}
              onChange={(value) => setValue("activeLow", value === "active_low")}
            />
          </FormField>
        </AdditionalFields>
      </>
    );
  }
  if (kind === "opcua") {
    return (
      <>
        <div className="gwConnectionFields">
          <FormField label="Server URL"><input className="gwInput" value={values.endpointUrl} onChange={(event) => setValue("endpointUrl", event.target.value)} placeholder="opc.tcp://10.0.0.22:4840" /></FormField>
          <FormField label="Node ID"><input className="gwInput" value={values.nodeId} onChange={(event) => setValue("nodeId", event.target.value)} placeholder="ns=2;s=Machine.Speed" /></FormField>
          <FormField label="Namespace URI" help="Recommended so namespace changes cannot point this connection at the wrong node.">
            <input className="gwInput" value={values.namespaceUri} onChange={(event) => setValue("namespaceUri", event.target.value)} placeholder="urn:plant:machine" />
          </FormField>
        </div>
        <FormField label="Trusted server certificate" help="Paste the certificate provided by the OPC UA server." className="gwConnectionWideField">
          <textarea className="gwInput" value={values.serverCertPEM} onChange={(event) => setValue("serverCertPEM", event.target.value)} rows={5} placeholder="-----BEGIN CERTIFICATE-----" />
        </FormField>
      </>
    );
  }
  if (kind === "enip-class1") {
    return (
      <>
        <div className="gwConnectionFields">
          <FormField label="Device model"><input className="gwInput" value="Turck TBEN-S1-8DXP" readOnly aria-readonly="true" /></FormField>
          <FormField label="Device host"><input className="gwInput" value={values.host} onChange={(event) => setValue("host", event.target.value)} placeholder="10.0.0.30" /></FormField>
          <FormField label="Digital channel" help="DX0 through DX7. Addressing is automatic.">
            <NudgeNumber value={values.channel} onChange={(value) => setValue("channel", value)} min={0} max={7} aria-label="Digital channel" />
          </FormField>
          <FormField label="ODVA vendor ID" help="Use your organization’s assigned ID for customer deployments.">
            <NudgeNumber value={values.originatorVendorId} onChange={(value) => setValue("originatorVendorId", value)} min={1} max={65535} aria-label="ODVA vendor ID" />
          </FormField>
        </div>
        <AdditionalFields>
          <FormField label="Cycle interval (ms)"><NudgeNumber value={values.rpiMs} onChange={(value) => setValue("rpiMs", value)} min={1} max={1000} aria-label="Cycle interval (ms)" /></FormField>
          <FormField label="TCP port"><NudgeNumber value={values.tcpPort} onChange={(value) => setValue("tcpPort", value)} min={1} max={65535} aria-label="TCP port" /></FormField>
          <FormField label="I/O UDP port"><NudgeNumber value={values.targetUDPPort} onChange={(value) => setValue("targetUDPPort", value)} min={1} max={65535} aria-label="I/O UDP port" /></FormField>
          <FormField label="Operation timeout (ms)"><NudgeNumber value={values.operationTimeoutMs} onChange={(value) => setValue("operationTimeoutMs", value)} min={100} max={30000} aria-label="Operation timeout (ms)" /></FormField>
        </AdditionalFields>
      </>
    );
  }
  if (kind === "ethercat") {
    const pointValue = `${direction}:${values.channel}`;
    return (
      <>
        <div className="gwConnectionFields">
          <FormField label="Device model"><input className="gwInput" value="Beckhoff EK1818" readOnly aria-readonly="true" /></FormField>
          <FormField label="Dedicated network interface" help="Connect the EK1818 directly. Keep normal plant traffic off this interface.">
            <input className="gwInput" value={values.interfaceName} onChange={(event) => setValue("interfaceName", event.target.value)} placeholder="enp2s0" />
          </FormField>
          <FormField label="I/O point" help="Choose the physical input or output. Direction and addressing are automatic.">
            <Select
              value={pointValue}
              options={ethercatPointOptions}
              onChange={(value) => {
                const [nextDirection, nextChannel] = String(value).split(":");
                setDirection(nextDirection === "output" ? "output" : "input");
                setValue("channel", nextChannel);
              }}
            />
          </FormField>
        </div>
        <AdditionalFields>
          <FormField label="Device revision" help="The current EK1818 normally uses 0x00120000.">
            <input className="gwInput" value={values.deviceRevision} onChange={(event) => setValue("deviceRevision", event.target.value)} placeholder="0x00120000" />
          </FormField>
          <FormField label="Cycle interval (ms)"><NudgeNumber value={values.cycleTimeMs} onChange={(value) => setValue("cycleTimeMs", value)} min={1} max={1000} aria-label="Cycle interval (ms)" /></FormField>
          <FormField label="Watchdog (ms)"><NudgeNumber value={values.watchdogMs} onChange={(value) => setValue("watchdogMs", value)} min={2} max={60000} aria-label="Watchdog (ms)" /></FormField>
        </AdditionalFields>
      </>
    );
  }
  if (kind === "profinet") {
    return (
      <>
        <div className="gwConnectionFields">
          <FormField label="Device model"><input className="gwInput" value="Turck TBEN-S1-8DXP" readOnly aria-readonly="true" /></FormField>
          <FormField label="Network interface" help="The Ethernet interface connected to the PROFINET network.">
            <input className="gwInput" value={values.interfaceName} onChange={(event) => setValue("interfaceName", event.target.value)} placeholder="enp2s0" />
          </FormField>
          <FormField label="Station name" help="Must exactly match the name assigned to the Turck device.">
            <input className="gwInput" value={values.stationName} onChange={(event) => setValue("stationName", event.target.value)} placeholder="turck-bench" />
          </FormField>
          <FormField label="Digital channel" help="Inputs and outputs 1 through 8; addressing is automatic.">
            <NudgeNumber value={values.channel} onChange={(value) => setValue("channel", value)} min={1} max={8} aria-label="Digital channel" />
          </FormField>
        </div>
        <AdditionalFields>
          <FormField label="Cycle interval (ms)" help="Start at 32 ms on a standard Linux device.">
            <NudgeNumber value={values.cycleTimeMs} onChange={(value) => setValue("cycleTimeMs", value)} min={1} max={512} aria-label="Cycle interval (ms)" />
          </FormField>
          <FormField label="Watchdog (ms)"><NudgeNumber value={values.watchdogMs} onChange={(value) => setValue("watchdogMs", value)} min={2} max={60000} aria-label="Watchdog (ms)" /></FormField>
        </AdditionalFields>
      </>
    );
  }
  return (
    <>
      <div className="gwConnectionFields">
        <FormField label="Secure broker URL"><input className="gwInput" value={values.url} onChange={(event) => setValue("url", event.target.value)} placeholder="mqtts://broker.example:8883" /></FormField>
        <FormField label="Topic"><input className="gwInput" value={values.topic} onChange={(event) => setValue("topic", event.target.value)} placeholder="line/one/speed" /></FormField>
        <FormField label="Client ID"><input className="gwInput" value={values.clientId} onChange={(event) => setValue("clientId", event.target.value)} /></FormField>
        <FormField
          label="JSON value path"
          help={direction === "output" ? "Whole-value commands only." : "Optional when the topic contains a JSON object."}
        >
          <input className="gwInput" value={values.valuePath} disabled={direction === "output"} onChange={(event) => setValue("valuePath", event.target.value)} />
        </FormField>
      </div>
      <FormField label="Broker CA certificate" help="Paste the CA certificate provided by the broker." className="gwConnectionWideField">
        <textarea className="gwInput" value={values.tlsCAPEM} onChange={(event) => setValue("tlsCAPEM", event.target.value)} rows={5} placeholder="-----BEGIN CERTIFICATE-----" />
      </FormField>
    </>
  );
}

/**
 * EdgeConnectorCreateForm builds a reviewed device connection on the selected
 * runtime. Most protocols map one tag to one point; Modbus configures one
 * device with all of its points so a serial slave is opened only once. New
 * ordinary tags remain pending until the shared confirmation step.
 */
export default function EdgeConnectorCreateForm({
  gateway,
  capabilities,
  gpioBoard,
  serialPorts = [],
  ncdUSBReceiverPorts = [],
  existingConnectorNames = [],
  initialKind = "",
  initialDraft = null,
  busy = false,
  notify,
  onCancel,
  onPreview,
}) {
  const [tagPath, setTagPath] = useState(String(initialDraft?.tag_path || ""));
  const [tagId, setTagId] = useState("");
  const [tagMode, setTagMode] = useState(initialDraft?.tag_mode === "new" ? "new" : "existing");
  const [newTagName, setNewTagName] = useState(String(initialDraft?.new_tag_name || ""));
  const [newTagType, setNewTagType] = useState(String(initialDraft?.new_tag_type || "boolean"));
  const [direction, setDirection] = useState(initialDraft?.direction === "output" ? "output" : "input");
  const [kind, setKind] = useState(initialKind);
  // Connections are presented as their tag — there is no user-facing
  // "connection name". A stable internal label is derived from the tag and
  // protocol because the spec still requires one.
  const [name, setName] = useState(String(initialDraft?.name || ""));
  const [settings, setSettings] = useState(() => initialConnectionSettings(initialKind, initialDraft));
  const [evidence, setEvidence] = useState(null);
  const [evidenceError, setEvidenceError] = useState("");
  const [checkingTag, setCheckingTag] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [error, setError] = useState("");
  const errorRef = useRef(null);

  const kindAvailability = useMemo(
    () => edgeConnectorKindAvailability(capabilities, gpioBoard),
    [capabilities, gpioBoard]
  );
  const kindOptions = useMemo(() => kindAvailability.selectable.map((item) => ({
    value: item.value,
    label: item.label,
  })), [kindAvailability]);
  const protocolLocked = Boolean(initialDraft?.connector_id || initialDraft?.legacy_binding_id);
  const protocolOptions = protocolLocked
    ? kindOptions.filter((item) => item.value === initialKind)
    : kindOptions;

  const gpioReady = kindAvailability.gpioReady;
  const eligibility = tagPath ? edgeConnectorTagEligibility(evidence, tagPath, direction) : null;
  const tagType = eligibility?.eligible ? eligibility.tagType : "";
  const kindInfo = EDGE_CONNECTOR_KINDS.find((item) => item.value === kind) || null;
  const kindSupported = !!kindInfo && kindAvailability.selectable.some((item) => item.value === kind);
  const selectedTagType = tagMode === "new" ? newTagType : tagType;
  const protocolTagError = connectorTagError({ kind, tagType: selectedTagType, direction });
  const formLocked = Boolean(busy || reviewing);
  const modbusDeviceWorkflow = ["modbus-tcp", "modbus-rtu", "modbus-rtu-over-tcp"].includes(kind);
  const ncdSensorWorkflow = kind === "ncd-pr63-27c-usb";
  const accessControl = protocolAccessControl(kind);

  useEffect(() => {
    setSettings(initialConnectionSettings(kind, initialDraft));
    // Protocol or reviewed-draft changes start a new form. Runtime inventory
    // refreshes do not reset an operator's in-progress values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDraft, kind]);

  // The profile can arrive on a heartbeat after the form opens. Select its
  // first real point only when the current default is not valid, and never
  // overwrite an operator's valid choice.
  useEffect(() => {
    if (kind !== "gpio") return;
    const pins = Array.isArray(gpioBoard?.available_pins)
      ? gpioBoard.available_pins.filter((pin) => pin?.policy !== "reserved")
      : [];
    if (!pins.length) return;
    const iotLink = gpioBoard?.profile === "compulab-iot-link-dio-v1";
    setSettings((current) => {
      const valid = pins.some((pin) => String(pin.bcm) === String(current.bcmPin));
      const next = { ...current };
      if (!valid) next.bcmPin = String(pins[0].bcm);
      if (iotLink) next.bias = "disabled";
      return next;
    });
  }, [gpioBoard, kind]);

  useEffect(() => {
    const capabilityCount = Array.isArray(capabilities) ? capabilities.length : 0;
    if (kind && capabilityCount > 0 && !kindSupported) setKind("");
  }, [capabilities, kind, kindSupported]);

  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);

  // Fresh exact-path evidence from the selected runtime for every picked tag:
  // eligibility (existence, type, writability) is proven, not assumed.
  useEffect(() => {
    setEvidence(null);
    setEvidenceError("");
    if (!tagPath) {
      // Clearing the picker cancels the old request. Reset explicitly because
      // that stale request's finally handler must not update this form.
      setCheckingTag(false);
      return undefined;
    }
    let cancelled = false;
    setCheckingTag(true);
    inspectEdgeTagCatalog(gateway.id, [tagPath])
      .then((payload) => { if (!cancelled) setEvidence(payload); })
      .catch(() => { if (!cancelled) setEvidenceError(`Could not verify this tag on ${gateway.name}. Try again.`); })
      .finally(() => { if (!cancelled) setCheckingTag(false); });
    return () => { cancelled = true; };
  }, [gateway.id, gateway.name, tagPath]);

  useEffect(() => {
    if (initialDraft?.name || !kindInfo) return;
    const base = tagMode === "new" ? tagSlug(newTagName) : lastPathSegment(tagPath);
    if (base) setName(`${base} · ${kindInfo.label}`);
  }, [tagPath, newTagName, tagMode, kindInfo, initialDraft]);

  const setValue = (field, value) => setSettings((current) => ({ ...current, [field]: value }));

  // Keep the inline tag type inside the selected protocol's mappable set.
  const newTagTypes = useMemo(
    () => {
      return (KIND_TAG_TYPES[kind] || []).filter((value) => {
        if (kind === "opcua" && direction === "output" && value === "json") return false;
        return true;
      });
    },
    [direction, kind]
  );
  useEffect(() => {
    if (newTagTypes.length && !newTagTypes.includes(newTagType)) setNewTagType(newTagTypes[0]);
  }, [newTagTypes, newTagType]);

  // Review is non-mutating. A new tag is represented in the review payload,
  // then created and declared to the runtime only after the user selects
  // Connect in the shared confirmation step.
  const review = async () => {
    setError("");
    if (!kind) { setError("Choose the device protocol."); return; }
    if (!kindSupported) {
      setError(kind === "gpio" && !gpioReady
        ? `${gateway.name} has not recognized its digital I/O yet.`
        : `${gateway.name} does not support ${kindInfo?.label || kind} connections.`);
      return;
    }
    const savedGPIOProfile = String(initialDraft?.settings?.boardProfile || "");
    if (kind === "gpio" && savedGPIOProfile && savedGPIOProfile !== String(gpioBoard?.profile || "")) {
      setError("This device no longer reports the digital I/O hardware used by this connection. Remove it and connect the current hardware instead.");
      return;
    }
    let path = tagPath;
    let effectiveTagType = tagType;
    let pendingTag = null;
    if (tagMode === "new") {
      const slug = tagSlug(newTagName);
      if (!slug) { setError("Name the new tag first."); return; }
      path = `default/${slug}`;
      effectiveTagType = newTagType;
      pendingTag = {
        fullPath: path,
        path: slug,
        name: newTagName.trim() || slug,
        type: TAG_TYPE_WIRE[newTagType],
        writable: direction === "output",
        description: name ? `Created for ${name}` : "",
        direction,
      };
    } else {
      if (!path) { setError("Choose the tag to connect first."); return; }
      if (!eligibility?.eligible) {
        setError(`${path}: ${eligibility?.message || evidenceError || "the device has not confirmed this tag yet."}`);
        return;
      }
    }
    let spec;
    try {
      spec = buildEdgeConnectorSpec({
        kind,
        name: name || `${lastPathSegment(path)} · ${kindInfo.label}`,
        connectorID: initialDraft?.connector_id || "",
        desiredState: initialDraft?.desired_state || "running",
        tagPath: path,
        tagType: effectiveTagType,
        direction,
        replacesLegacyGPIOBindingID: initialDraft?.legacy_binding_id || "",
        settings: kind === "gpio"
          ? { ...settings, boardProfile: gpioBoard?.profile || "", availablePins: gpioBoard?.available_pins || [] }
          : settings,
      });
    } catch (err) {
      setError(err.message);
      return;
    }
    if (pendingTag) {
      if (Number.isSafeInteger(initialDraft?.expected_revision)) {
        onPreview?.(spec, pendingTag, initialDraft.expected_revision);
      } else {
        onPreview?.(spec, pendingTag);
      }
      return;
    }
    setReviewing(true);
    try {
      const preview = await previewEdgeConnector(gateway.id, spec, initialDraft?.expected_revision);
      onPreview?.(preview, null);
    } catch (err) {
      const message = publicConnectionError(err, gateway.name);
      setError(message);
      notify?.({ type: "error", title: "Connection could not be prepared", message });
    } finally {
      setReviewing(false);
    }
  };

  return (
    <div className="gwEdgeProgramSection gwConnectionBuilder" role="form" aria-label={initialDraft?.connector_id ? "Edit I/O connection" : "New I/O connection"}>
      <header className="gwConnectionBuilderHeader">
        <div className="gwSectionTitle">
          {initialDraft?.connector_id
            ? (ncdSensorWorkflow ? `Manage sensor receiver · ${gateway.name}` : `Edit I/O connection · ${gateway.name}`)
            : `New I/O connection · ${gateway.name}`}
        </div>
        <div className="gwHint">{modbusDeviceWorkflow
          ? "Configure the Modbus device once, then add all of its values and outputs."
          : ncdSensorWorkflow && initialDraft?.connector_id
            ? "Review receiver settings and name sensors as they are detected."
            : ncdSensorWorkflow
              ? "Connect the wireless receiver. Sensor tags appear automatically after the first report."
            : kind
              ? "Connect tags to inputs and outputs on this device."
              : "Connect sensors, equipment, and tags on this device."}</div>
      </header>
      {initialDraft?.legacy_binding_id ? (
        <div className="gwNotice isSoft" role="note">
          This updates the existing GPIO mapping without releasing the pin.
        </div>
      ) : null}
      {error ? (
        <div className="gwAlert" role="alert" tabIndex={-1} ref={errorRef}>
          <div className="gwAlertTitle">Check the connection</div>
          <div className="gwAlertBody">{error}</div>
        </div>
      ) : null}

      <fieldset className="gwConnectionBuilderFields" disabled={formLocked}>
      {protocolOptions.length ? (
        <div className="gwConnectionFields">
          {/* An existing connection cannot change protocol, so the picker is a
              dead control while editing. The Modbus and sensor-receiver
              workflows name the protocol in their own headers; other kinds
              keep a read-only line so the protocol stays visible. */}
          {protocolLocked && ncdSensorWorkflow ? null : protocolLocked ? (
            <FormField label="Device protocol" help={kind ? PROTOCOL_DESCRIPTIONS[kind] : undefined}>
              <input className="gwInput" value={kindInfo?.label || kind} readOnly aria-readonly="true" />
            </FormField>
          ) : (
          <FormField label="Device protocol" help={kind ? PROTOCOL_DESCRIPTIONS[kind] : `Only protocols available on ${gateway.name} are listed.`}>
            <Select
              value={kind}
              options={protocolOptions}
              placeholder="Choose a protocol…"
              onChange={(value) => {
                setKind(value);
                setDirection("input");
                setSettings(initialConnectionSettings(value, null));
                setError("");
              }}
            />
          </FormField>
          )}
          {kindSupported && !modbusDeviceWorkflow && !ncdSensorWorkflow && accessControl ? (
            <FormField label={accessControl.label} help={accessControl.options.find((option) => option.value === direction)?.description}>
              <Select
                value={direction}
                options={accessControl.options}
                onChange={(value) => { setDirection(value); setError(""); }}
              />
            </FormField>
          ) : null}
        </div>
      ) : (
        <div className="gwNotice isSoft">No device protocols are available yet. Update this runtime, then refresh the page.</div>
      )}

      {kindSupported && modbusDeviceWorkflow ? (
        <EdgeModbusDeviceForm
          key={kind}
          gateway={gateway}
          kind={kind}
          serialPorts={serialPorts}
          initialDraft={initialDraft}
          busy={formLocked}
          notify={notify}
          onCancel={onCancel}
          onPreview={onPreview}
        />
      ) : kindSupported && ncdSensorWorkflow ? (
        <EdgeNCDSensorForm
          gateway={gateway}
          capabilities={capabilities}
          ncdUSBReceiverPorts={ncdUSBReceiverPorts}
          existingConnectorNames={existingConnectorNames}
          initialDraft={initialDraft}
          busy={formLocked}
          notify={notify}
          onCancel={onCancel}
          onPreview={onPreview}
        />
      ) : kindSupported ? (
        <>
          <div className="gwConnectionFormGroup">
            <div className="gwLabel">Device point</div>
            <ProtocolFields
              kind={kind}
              direction={direction}
              setDirection={setDirection}
              tagType={selectedTagType}
              gpioBoard={gpioBoard}
              values={settings}
              setValue={setValue}
            />
          </div>

          <div className="gwConnectionFormGroup gwConnectionTagPanel">
            <FormField
              label="Tag"
              className="gwConnectionWideField"
            >
              {tagMode === "existing" ? (
                <>
                  <div className="gwConnectionTagPickerRow">
                    <TagPicker
                      value={tagId}
                      displayValue={tagPath}
                      placeholder="Search or choose a tag…"
                      disabled={formLocked}
                      onChange={(nextId, fullPath) => { setTagId(nextId); setTagPath(fullPath || ""); setError(""); }}
                    />
                    <button
                      type="button"
                      className="gwSecondaryBtn"
                      onClick={() => { setTagMode("new"); setError(""); }}
                    >
                      Create new tag
                    </button>
                  </div>
                  {checkingTag ? <small className="gwHint" role="status">Checking this tag on {gateway.name}…</small> : null}
                  {!checkingTag && tagPath && eligibility ? (
                    <small className={eligibility.eligible ? "gwHint isPositive" : "gwHint isWarning"} role="status">
                      {eligibility.eligible
                        ? `Ready on ${gateway.name} · ${tagTypeLabel(tagType)}${direction === "output" ? " · Writable" : ""}`
                        : eligibility.message}
                    </small>
                  ) : null}
                  {protocolTagError ? <small className="gwHint isWarning" role="status">{protocolTagError}</small> : null}
                  {!checkingTag && evidenceError ? <small className="gwHint isWarning" role="status">{evidenceError}</small> : null}
                </>
              ) : (
                <>
                  <div className="gwConnectionTagCreateHeader">
                    <span>
                      <strong>Create a new tag</strong>
                      <small>It will be added to the Tags panel and made available on {gateway.name}.</small>
                    </span>
                    <button
                      type="button"
                      className="gwGhostBtn"
                      onClick={() => { setTagMode("existing"); setError(""); }}
                    >
                      Choose existing instead
                    </button>
                  </div>
                  <div className="gwConnectionNewTagFields">
                    <input
                      className="gwInput"
                      value={newTagName}
                      placeholder="New tag name"
                      onChange={(event) => { setNewTagName(event.target.value); setError(""); }}
                    />
                    {newTagTypes.length > 1 ? (
                      <Select
                        value={newTagType}
                        options={newTagTypes.map((value) => ({ value, label: TAG_TYPE_LABELS[value] }))}
                        onChange={setNewTagType}
                      />
                    ) : null}
                  </div>
                  <small className="gwHint">
                    Creates {`default/${tagSlug(newTagName) || "…"}`}
                    {newTagTypes.length === 1 ? ` as a ${TAG_TYPE_LABELS[newTagTypes[0]].toLowerCase()} tag` : ""}
                    {direction === "output" ? " that can be written" : ""} when you continue.
                  </small>
                  {protocolTagError ? <small className="gwHint isWarning" role="status">{protocolTagError}</small> : null}
                </>
              )}
            </FormField>
          </div>
        </>
      ) : null}
      </fieldset>

      {!modbusDeviceWorkflow && !ncdSensorWorkflow ? <footer className="gwConnectionBuilderFooter">
        <div className="gwHint">
          {tagMode === "new"
            ? `Connect creates the tag, then sends this connection to ${gateway.name}.`
            : `Connect checks these settings, then sends this connection to ${gateway.name}.`}
        </div>
        <div className="gwHeaderActions">
          <button type="button" className="gwGhostBtn" disabled={busy || reviewing} onClick={onCancel}>Cancel</button>
          <button
            type="button"
            className="gwPrimaryBtn"
            disabled={busy || reviewing || !kind || !kindSupported ||
              Boolean(protocolTagError) ||
              (tagMode === "existing" ? !eligibility?.eligible : !tagSlug(newTagName))}
            onClick={review}
          >
            {reviewing || busy
              ? (initialDraft?.connector_id ? "Saving…" : "Connecting…")
              : initialDraft?.connector_id ? "Save changes" : "Connect"}
          </button>
        </div>
      </footer> : null}
    </div>
  );
}
